/* eslint-disable no-nested-ternary */
// src/components/dentally/DentallyBookingSuccess.jsx
import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Alert, Divider, Stack, Collapse } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import Iconify from '../Iconify';
import PaymentLinkTracker from './PaymentLinkTracker';
import { createDentallyAppointmentAsync } from '../../utils/dentallyApi';
import { callApi } from '../../utils/apiCall';
import { parseDentallyDate } from '../../utils/dentallyUtils';

// ── Internal step labels (shown in expandable detail) ──────────────────
const STEP_LABELS = {
  dentally_patient_created: 'Patient created in Dentally',
  lead_created: 'Lead created in CRM',
  treatment_created: 'Treatment added',
  crm_appointment_created: 'Appointment booked in CRM',
  dentally_appointment_created: 'Appointment booked in Dentally',
  payment_link_sent: 'Payment link sent',
  whatsapp_link_sent: 'Payment link sent via WhatsApp',
  payment_received: 'Payment recorded',
  dentally_booking_deferred: 'Booking saved — awaiting payment',
  dentally_reminder_created: 'Payment reminder created',
};

const formatApptTime = (dateStr) => {
  if (!dateStr) return null;
  try {
    const d = parseDentallyDate(dateStr);
    if (!d) return null;
    return d.toLocaleString('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return null; }
};

// ── Derive user-meaningful summary steps from raw backend steps ────────
const getSummarySteps = (steps, apptTime, showDentallyFailed) => {
  const summary = [];
  // Appointment booked is always the first meaningful step
  if (steps.includes('crm_appointment_created') || steps.includes('dentally_appointment_created')) {
    summary.push({
      icon: 'mdi:calendar-check',
      label: apptTime ? `Appointment booked — ${apptTime}` : 'Appointment booked',
      color: '#00AB55',
    });
  }
  // Dentally warning as a summary-level item
  if (showDentallyFailed) {
    summary.push({
      icon: 'mdi:alert-circle',
      label: 'Dentally sync needs attention',
      color: '#FFAB00',
    });
  }
  // Payment
  if (steps.includes('payment_link_sent') || steps.includes('whatsapp_link_sent')) {
    summary.push({
      icon: 'mdi:credit-card-outline',
      label: 'Payment link sent to patient',
      color: '#00AB55',
    });
  } else if (steps.includes('payment_received')) {
    summary.push({
      icon: 'mdi:check-decagram',
      label: 'Payment recorded',
      color: '#00AB55',
    });
  }
  return summary;
};

export default function DentallyBookingSuccess({ result, practiceId, onViewLead, onBookAnother, onClose }) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [isPaid, setIsPaid] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [syncRetrying, setSyncRetrying] = useState(false);
  const [syncResult, setSyncResult] = useState(null); // 'success' | 'failed' | null
  const [showDetail, setShowDetail] = useState(false);

  // Derive isDeferred before the early return so it can be used in hooks below
  const isDeferred = result?.isDeferred ?? false;

  // Wrap onClose to fire an informational toast for deferred bookings
  const handleClose = useCallback(() => {
    if (isDeferred) {
      const name = result?.leadName || 'Lead';
      const leadDetailPath = practiceId && result?.leadId
        ? `/dashboard/practice/${practiceId}/lead-detail/${result.leadId}`
        : null;
      enqueueSnackbar(
        `${name} has not moved status — confirming payment to book`,
        {
          variant: 'info',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          autoHideDuration: 10000,
          action: leadDetailPath ? () => (
            <Button
              size="small"
              sx={{ color: 'inherit', fontWeight: 700, ml: 1, whiteSpace: 'nowrap' }}
              onClick={() => navigate(leadDetailPath)}
            >
              View Lead
            </Button>
          ) : undefined,
        },
      );
    }
    onClose?.();
  }, [isDeferred, result?.leadName, result?.leadId, practiceId, navigate, enqueueSnackbar, onClose]);

  const handleRetrySync = useCallback(async () => {
    if (!result?.dentallyPayload || !practiceId) return;
    setSyncRetrying(true);
    setSyncResult(null);
    try {
      const res = await createDentallyAppointmentAsync(practiceId, { ...result.dentallyPayload, state: 'Pending' });
      if (res?.ok) {
        setSyncResult('success');
        try {
          const metaRes = await callApi(`/api/v1/practices/${practiceId}/leads/${result.leadId}/`);
          const existing = Array.isArray(metaRes?.data?.lead_metadata) ? metaRes.data.lead_metadata : [];
          const merged = [...existing];
          const idx = merged.findIndex(m => m.key === 'pending_dentally_sync');
          if (idx !== -1) merged[idx] = { ...merged[idx], value: 'complete' };
          await callApi(`/api/v1/practices/${practiceId}/leads/${result.leadId}/`, 'patch', { lead_metadata: merged });
        } catch { /* non-critical */ }
      } else {
        setSyncResult('failed');
      }
    } catch {
      setSyncResult('failed');
    } finally {
      setSyncRetrying(false);
    }
  }, [result?.dentallyPayload, result?.leadId, practiceId]);

  const handleMarkAsPaid = useCallback(async () => {
    if (!practiceId || !result?.leadId || !result?.treatmentRecordId || !result?.paymentRecordId) return;
    setMarkingPaid(true);
    try {
      const res = await callApi(
        `/api/v1/practices/${practiceId}/leads/${result.leadId}/treatments/${result.treatmentRecordId}/payments/${result.paymentRecordId}/`,
        'patch',
        { paid_at: new Date().toISOString(), mode_of_payment: 'cash' },
      );
      if (res.ok) {
        setIsPaid(true);
      }
    } catch { /* fallback — still toggle UI so user isn't stuck */ }
    setMarkingPaid(false);
  }, [practiceId, result?.leadId, result?.treatmentRecordId, result?.paymentRecordId]);

  if (!result?.success) return null;

  const apptTime = formatApptTime(result.startTime);
  const rawSteps = result.steps ?? [];
  const steps = syncResult === 'success' && !rawSteps.includes('dentally_appointment_created')
    ? [...rawSteps, 'dentally_appointment_created']
    : rawSteps;
  const showDentallyFailed = result.dentallyWarning && syncResult !== 'success' && !steps.includes('dentally_appointment_created');
  const hasPaymentLink = steps.includes('payment_link_sent') || steps.includes('whatsapp_link_sent');
  const hasWarnings = !!(result.dentallyWarning && syncResult !== 'success') || !!result.paymentWarning;

  // ── Heading state ──────────────────────────────────────────────────
  const headingText = isDeferred
    ? 'Payment Link Sent — Booking Pending'
    : hasWarnings ? 'Appointment Saved — Action Needed' : 'Appointment Confirmed';
  const headingColor = isDeferred ? '#FFAB00' : hasWarnings ? '#FFAB00' : '#00AB55';
  const headingIcon = isDeferred ? 'mdi:cash-clock' : hasWarnings ? 'mdi:alert-circle' : 'mdi:check-circle';
  const headingBg = isDeferred ? 'rgba(255,171,0,0.1)' : hasWarnings ? 'rgba(255,171,0,0.1)' : 'rgba(0,171,85,0.1)';

  // ── Summary steps ──────────────────────────────────────────────────
  const summarySteps = getSummarySteps(steps, apptTime, showDentallyFailed);

  // ── Footer button logic ────────────────────────────────────────────
  const primaryLabel = isDeferred
    ? 'Close & Track Payment'
    : hasPaymentLink ? (isPaid ? 'Done' : 'Close & Track Payment') : 'Done';

  return (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      {/* ─── Heading zone ─── */}
      <Box sx={{
        width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 1.5,
        bgcolor: headingBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background-color 0.4s ease',
      }}>
        <Iconify icon={headingIcon} width={32} sx={{ color: headingColor, transition: 'color 0.4s ease' }} />
      </Box>
      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: headingColor, transition: 'color 0.4s ease' }}>
        {headingText}
      </Typography>

      {apptTime && !hasWarnings && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {apptTime}
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />

      {isDeferred && (
        <Alert
          severity="warning"
          icon={<Iconify icon="mdi:information-outline" width={20} />}
          sx={{ textAlign: 'left', mb: 2 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Lead stays as New until payment confirmed
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {result.steps?.includes('dentally_reminder_created')
              ? 'A reminder task has been created. Once the patient pays, go to the lead overview to complete the Dentally booking.'
              : 'Once the patient pays, go to the lead overview to complete the Dentally booking.'}
          </Typography>
        </Alert>
      )}

      {/* ─── Summary steps (user-meaningful) ─── */}
      <Box sx={{ textAlign: 'left', mb: 2 }}>
        {summarySteps.map((step, idx) => (
          <Stack
            key={step.label}
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              mb: 0.75,
              animation: 'fadeSlideIn 0.3s ease both',
              animationDelay: `${idx * 80}ms`,
              '@keyframes fadeSlideIn': {
                from: { opacity: 0, transform: 'translateY(6px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <Iconify icon={step.icon} width={18} sx={{ color: step.color, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>{step.label}</Typography>
          </Stack>
        ))}
      </Box>

      {/* ─── Expandable detail toggle ─── */}
      {steps.length > 0 && (
        <Box sx={{ textAlign: 'left', mb: 2 }}>
          <Typography
            variant="caption"
            onClick={() => setShowDetail(prev => !prev)}
            sx={{
              color: 'text.disabled',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              '&:hover': { color: 'text.secondary' },
              transition: 'color 0.2s ease',
              userSelect: 'none',
            }}
          >
            <Iconify
              icon={showDetail ? 'mdi:chevron-up' : 'mdi:chevron-down'}
              width={16}
              sx={{ transition: 'transform 0.2s ease' }}
            />
            {showDetail ? 'Hide details' : `Show details (${steps.length} steps)`}
          </Typography>
          <Collapse in={showDetail} timeout={250}>
            <Box sx={{ mt: 1, pl: 0.5 }}>
              {steps.map(step => (
                <Stack key={step} direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                  <Iconify icon="mdi:check" width={14} sx={{ color: '#00AB55', flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary">{STEP_LABELS[step] || step}</Typography>
                </Stack>
              ))}
              {showDentallyFailed && (
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                  <Iconify icon="mdi:alert" width={14} sx={{ color: '#FFAB00', flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary">Could not book in Dentally</Typography>
                </Stack>
              )}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* ─── Error recovery zone ─── */}
      {result.dentallyWarning && syncResult !== 'success' && (
        <Alert
          severity={syncResult === 'failed' ? 'error' : 'warning'}
          sx={{ textAlign: 'left', mb: 2 }}
          action={result.dentallyPayload && (
            <LoadingButton
              size="small"
              loading={syncRetrying}
              onClick={handleRetrySync}
              sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              Retry Sync
            </LoadingButton>
          )}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Dentally appointment not created
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            Your appointment is saved in the CRM but could not be booked in Dentally.
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            Reason: {result.dentallyWarning}
          </Typography>
          {syncResult === 'failed' && (
            <Typography variant="caption" color="error" component="div" sx={{ mt: 0.5 }}>
              Retry failed — you can also retry from the lead detail page.
            </Typography>
          )}
        </Alert>
      )}
      {syncResult === 'success' && (
        <Alert severity="success" sx={{ textAlign: 'left', mb: 2 }}>
          Dentally sync successful — appointment is now booked in Dentally.
        </Alert>
      )}

      {result.paymentWarning && (
        <Alert severity="warning" sx={{ textAlign: 'left', mb: 2 }}>
          Booking confirmed. Payment link failed — {result.paymentWarning}
        </Alert>
      )}

      {/* ─── Payment tracker zone (below divider, only when relevant) ─── */}
      {hasPaymentLink &&
        practiceId && result.leadId && result.treatmentRecordId && result.paymentRecordId && (
        <>
          <Divider sx={{ my: 2 }} />
          <PaymentLinkTracker
            practiceId={practiceId}
            leadId={result.leadId}
            treatmentId={result.treatmentRecordId}
            paymentId={result.paymentRecordId}
            contactEmail={result.contactEmail}
            contactPhone={result.contactPhone}
            checkoutUrl={result.checkoutUrl}
            linkChannel={result.paymentLinkChannel}
            onPaymentStatusChange={(status) => setIsPaid(status === 'paid')}
            externalPaid={isPaid}
            onMarkAsPaid={handleMarkAsPaid}
            markingPaid={markingPaid}
          />
        </>
      )}

      {/* ─── Footer: max 2 buttons ─── */}
      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', alignItems: 'center', mt: 3 }}>
        {onViewLead && result.leadId != null && (
          <Button variant="outlined" onClick={() => onViewLead(result.leadId)} sx={{ fontWeight: 500 }}>
            View Lead
          </Button>
        )}
        {onClose ? (
          <Button
            variant="contained"
            color="primary"
            onClick={handleClose}
            sx={{ minWidth: 140, fontWeight: 600 }}
          >
            {primaryLabel}
          </Button>
        ) : (
          <Button variant="contained" onClick={onBookAnother} sx={{ minWidth: 140, fontWeight: 600 }}>
            Book Another
          </Button>
        )}
      </Box>
    </Box>
  );
}

DentallyBookingSuccess.propTypes = {
  result: PropTypes.shape({
    success: PropTypes.bool,
    leadId: PropTypes.number,
    leadName: PropTypes.string,
    treatmentRecordId: PropTypes.number,
    paymentRecordId: PropTypes.number,
    checkoutUrl: PropTypes.string,
    contactEmail: PropTypes.string,
    contactPhone: PropTypes.string,
    paymentLinkChannel: PropTypes.string,
    paymentAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    steps: PropTypes.arrayOf(PropTypes.string),
    dentallyWarning: PropTypes.string,
    dentallyPayload: PropTypes.object,
    paymentWarning: PropTypes.string,
    startTime: PropTypes.string,
    isDeferred: PropTypes.bool,
  }).isRequired,
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onViewLead: PropTypes.func,
  onBookAnother: PropTypes.func,
  onClose: PropTypes.func,
};

DentallyBookingSuccess.defaultProps = {
  practiceId: null,
  onViewLead: null,
  onBookAnother: null,
  onClose: null,
};
